import type { ComponentProps, ReactNode } from "react"
import cn from "@/v2-ui/utils/utils.cn"
import Icon, { type IconProps } from "@/v2-ui/icon"
import Tooltip from "@/v2-ui/tooltip"

export type BadgeVariant =
  | "neutral"
  | "positive"
  | "positive-strong"
  | "warning"
  | "critical"
  | "outline"

export type BadgeSize = "tiny" | "xs" | "sm" | "md"

// Omit strips React's global `content?: string` HTML attribute so the
// ReactNode-typed tooltip content can't intersect with it (same fix as
// TooltipButton/TooltipLink).
export type BadgeProps = Omit<ComponentProps<"span">, "content"> & {
  variant?: BadgeVariant
  size?: BadgeSize
  /** Icon rendered inside the pill, before the children. */
  iconSrc?: IconProps["src"]
  /**
   * Rich hover content — the whole pill is the trigger. Rendered on the
   * popover-styled panel surface (not the inverted default) so nested
   * `text-muted-foreground` / semantic colors stay readable.
   */
  tooltip?: ReactNode
  /** Renders the pill as a link. External URLs open in a new tab. */
  href?: string
}

const ICON_SIZES: Record<BadgeSize, number> = {
  tiny: 10,
  xs: 12,
  sm: 12,
  md: 14,
}

/**
 * THE badge for the backoffice: one tinted-pill language for every
 * status signal (usage score/trend, churn risk, automation, license
 * seats, uptime latency, invitation status, ...). Variants live in
 * badge.css; the hue alone carries the meaning — green good, amber
 * attention, red act-now, grey no-signal.
 */
function Badge(props: BadgeProps) {
  const {
    variant = "neutral",
    size = "sm",
    iconSrc,
    tooltip,
    href,
    className,
    children,
    ...spanProps
  } = props

  const pillClassName = cn(
    "badge",
    `badge-${variant}`,
    `badge-${size}`,
    tooltip != null && !href && "cursor-help",
    className,
  )

  const content = (
    <>
      {iconSrc && <Icon src={iconSrc} size={ICON_SIZES[size]} />}
      {children}
    </>
  )

  const isExternal = href?.includes("//")
  const pill = href ? (
    <a
      href={href}
      target={isExternal ? "_blank" : undefined}
      rel={isExternal ? "noopener noreferrer" : undefined}
      className={pillClassName}
      {...(spanProps as ComponentProps<"a">)}
    >
      {content}
    </a>
  ) : (
    <span className={pillClassName} {...spanProps}>
      {content}
    </span>
  )

  if (!tooltip) return pill

  return (
    <Tooltip>
      <Tooltip.Trigger asChild>{pill}</Tooltip.Trigger>
      <Tooltip.Content side="top" className="ui-tooltip-content--panel">
        <div className="whitespace-pre-line px-2.5 py-2 text-xs">{tooltip}</div>
      </Tooltip.Content>
    </Tooltip>
  )
}

export default Badge
